import {TanStackDevtools} from "@tanstack/react-devtools";
import {FormDevtoolsPanel} from "@tanstack/react-form-devtools";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtoolsPanel} from "@tanstack/react-query-devtools";
import {RouterProvider, createRouter} from "@tanstack/react-router";
import {TanStackRouterDevtoolsPanel} from "@tanstack/react-router-devtools";
import {StrictMode, useReducer} from "react";
import ReactDOM from "react-dom/client";
import {DefaultComponent} from "~/components/routerHelpers/defaultComponent";
import {DefaultErrorComponent} from "~/components/routerHelpers/defaultErrorComponent";
import {DefaultNotFoundComponent} from "~/components/routerHelpers/defaultNotFoundComponent";
import {DefaultPendingComponent} from "~/components/routerHelpers/defaultPendingComponent";
import {routeTree} from "~/routeTree.gen";
import {AnankeClient, AnankeClientContext} from "@vesta/ananke/contexts/anankeClientContext";
import {ChevronUp} from "react-bootstrap-icons";
import {IntellsysDevtoolsClient, IntellsysDevtoolsClientContext} from "~/contexts/intellsysDevtoolsClientContext";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {twJoin} from "tailwind-merge";
import {uniqueUuid} from "@vesta/unique/unique";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {useHotkeys} from "@tanstack/react-hotkeys";
import {parseHotkey} from "@tanstack/hotkeys";
import {ConnectionAttemptClient, ConnectionAttemptClientContext} from "~/contexts/connectionAttemptClientContext";
import {trackRouterPageViews} from "~/applicationAnalytics/routerTracking";
import {trackInitialPageLoad} from "~/applicationAnalytics/initialPageLoad";
import {argusPageTypeName} from "~/applicationAnalytics/argusPageTypeName";
import {getWorkspaceConfigManifest, hasWorkspaceUiConfig} from "~/workspaceConfig/configRuntime";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {resolveFixedArgusPageContext} from "~/applicationAnalytics/resolveFixedPageContext";

export interface IntellsysRouterContext {
    anankeClient: AnankeClient;
    connectionAttemptClient: ConnectionAttemptClient;
    environment: string;
    intellsysDevtoolsClient: IntellsysDevtoolsClient;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    oidcAuthorizeBaseUrl: string;
    oidcClientId: string;
    posthogKey: string;
    posthogProjectId: string;
    posthogHost: string;
    posthogClientId: string;
    sancusStorybookAccessToken: string;
}

const intellsysDevtoolsClient = new IntellsysDevtoolsClient();
const connectionAttemptClient = new ConnectionAttemptClient();

const anankeClient = new AnankeClient();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
        },
        mutations: {
            retry: false,
        },
    },
});

// Create a new router instance
const router = createRouter({
    routeTree: routeTree,
    context: {
        anankeClient: anankeClient,
        connectionAttemptClient: connectionAttemptClient,
        environment: import.meta.env.ENVIRONMENT,
        intellsysDevtoolsClient: intellsysDevtoolsClient,
        queryClient: queryClient,
        intellsysBaseUrl: import.meta.env.INTELLSYS_BASE_URL,
        oidcAuthorizeBaseUrl: import.meta.env.OIDC_AUTHORIZE_BASE_URL,
        oidcClientId: import.meta.env.OIDC_CLIENT_ID,
        posthogKey: import.meta.env.POSTHOG_KEY,
        posthogProjectId: import.meta.env.POSTHOG_PROJECT_ID,
        posthogHost: import.meta.env.POSTHOG_HOST,
        posthogClientId: import.meta.env.POSTHOG_CLIENT_ID,
        sancusStorybookAccessToken: import.meta.env.SANCUS_STORYBOOK_ACCESS_TOKEN,
    },
    caseSensitive: true,
    // defaultPreload: "intent",
    defaultPreload: false,
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
    // Parent beforeLoad hooks run on every navigation, even when loader data is
    // fresh. Delay pending UI so fast cached checks do not replace shared layouts.
    defaultPendingMs: 1000,
    defaultPendingMinMs: 500,
    trailingSlash: "never",
    defaultStaleTime: 0,
    defaultGcTime: 0,
    defaultComponent: DefaultComponent,
    defaultErrorComponent: DefaultErrorComponent,
    defaultPendingComponent: DefaultPendingComponent,
    defaultNotFoundComponent: DefaultNotFoundComponent,
});

function removeBootLoader(): void {
    document.getElementById("boot-loading-overlay")?.remove();
}

const unsubscribeBootLoaderRemoval = router.subscribe("onRendered", () => {
    removeBootLoader();
    unsubscribeBootLoaderRemoval();
});

//#region Argus
type WorkspaceAnalyticsData = {
    workspace: {id: Uuid; displayName: string};
    currentWorkspaceUser: {templateId: Uuid | null};
    userTemplates: Array<UserTemplate>;
};

trackRouterPageViews(router, (pathname) => {
    const workspaceMatch = router.state.matches.find((match) =>
        match.routeId === "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId",
    );
    const data = workspaceMatch?.loaderData as WorkspaceAnalyticsData | undefined;
    const configMatch = router.state.matches.find((match) =>
        match.routeId === "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_configScaffold/$pageKey",
    );
    const configuredPage = data !== undefined && configMatch !== undefined && hasWorkspaceUiConfig(data.workspace.id)
        ? getWorkspaceConfigManifest(data.workspace.id).pages[configMatch.params.pageKey]
        : undefined;
    return {
        workspace: data === undefined ? null : {workspaceId: data.workspace.id, workspaceName: data.workspace.displayName},
        role: data === undefined ? null : data.currentWorkspaceUser.templateId === null
            ? "-"
            : data.userTemplates.find((template) => template.id === data.currentWorkspaceUser.templateId)?.displayName ?? "?",
        page: configuredPage === undefined ? resolveFixedArgusPageContext(pathname) : {
            pageId: configuredPage.id,
            pageName: configuredPage.title,
            pageType: argusPageTypeName(configuredPage.type),
        },
    };
});
trackInitialPageLoad();
//#endregion

// #region Experimenting with modifying the route tree
// console.log(routeTree);
// function recurse(route) {
//     // if (x.isRoot === true) {}

//     console.log(route);
//     console.info(route.id);
//     console.info(route._id);
//     if (route.options.beforeLoad !== undefined) {
//         route.options = (args) => {
//             console.info("beforeLoad", args);
//             return route.options(args);
//         };
//     }

//     if (route.children === undefined) {
//         console.log("undefined");
//         return;
//     }

//     for (const childRoute of route.children) {
//         recurse(childRoute);
//     }
// }
// recurse(routeTree);
// #endregion

// Register the router instance for type safety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

// Render the app
const rootElement = document.getElementById("root");
if (rootElement !== null) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
        <StrictMode>
            <IntellsysDevtoolsClientContext.Provider value={intellsysDevtoolsClient}>
                <ConnectionAttemptClientContext.Provider value={connectionAttemptClient}>
                    <AnankeClientContext.Provider value={anankeClient}>
                        <QueryClientProvider client={queryClient}>
                            <RouterProvider router={router} />
                        </QueryClientProvider>
                    </AnankeClientContext.Provider>
                </ConnectionAttemptClientContext.Provider>
            </IntellsysDevtoolsClientContext.Provider>

            <IntellsysTanstackDevtools
                queryClient_={queryClient}
                router_={router}
            />

            <IntellsysDevtoolsPanel
                anankeClient_={anankeClient}
                intellsysDevtoolsClient_={intellsysDevtoolsClient}
                // queryClient_={queryClient}
                router_={router}
            />
        </StrictMode>,
    );
}

// TODO: Shift to dedicated file?
function IntellsysTanstackDevtools({
    queryClient_,
    router_,
}: {
    queryClient_: QueryClient;
    router_: typeof router;
    //
}) {
    return (
        <TanStackDevtools
            config={{
                // position: "middle-right",
                customTrigger: () => (
                    <div className="fixed right-0 bottom-[8rem]">
                        <div className="origin-bottom-right -rotate-90 text-[0.75rem] leading-normal">
                            <div className="grid grid-cols-[auto_auto] items-center gap-2">
                                <div>Devtools</div>
                                <ChevronUp className="size-2" />
                            </div>
                        </div>
                    </div>
                ),
                // inspectHotkey: ["Shift", "CtrlOrMeta"],
                // openHotkey: ["Shift", "CtrlOrMeta" + "A"],
            }}
            plugins={[
                {
                    name: "Tanstack Router",
                    render: <TanStackRouterDevtoolsPanel router={router_} />,
                },
                {
                    name: "Tanstack Query",
                    render: <ReactQueryDevtoolsPanel client={queryClient_} />,
                },
                {
                    name: "Tanstack Form",
                    render: <FormDevtoolsPanel />,
                },
                // formDevtoolsPlugin(),
            ]}
        />
    );
}

// TODO: Shift to dedicated file
function IntellsysDevtoolsPanel({
    anankeClient_,
    intellsysDevtoolsClient_,
    // queryClient_,
    router_,
}: {
    anankeClient_: AnankeClient;
    intellsysDevtoolsClient_: IntellsysDevtoolsClient;
    // queryClient_: QueryClient;
    router_: typeof router;
}) {
    if (import.meta.env.ENVIRONMENT === "prod") {
        return;
    }

    // Force re-render when configuration changes
    const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

    const toggleTheme = () => {
        anankeClient_.setConfiguration((prev) => ({
            ...prev,
            theme: prev.theme === "light" ? "dark" : "light",
        }));

        // Since we are not subscribed to changes in intellsysDevtoolsClient in this component,
        // the UI will be stale unless we trigger a force update
        forceUpdate();
    };

    const invalidateRouter = () => {
        router_.invalidate({
            forcePending: true,
        });
        // TODO: Invalidate most things from react query as well?
        // TODO: Show success flag or something
    };

    const signOut = () => {
        router_.navigate({to: "/sign-out"});
    };

    const cycleFillDummyData = () => {
        intellsysDevtoolsClient_.setConfiguration((prev) => ({
            ...prev,
            fillDummyData: !prev.fillDummyData,
        }));

        // Since we are not subscribed to changes in intellsysDevtoolsClient in this component,
        // the UI will be stale unless we trigger a force update
        forceUpdate();
    };

    const cycleMockApiCalls = () => {
        intellsysDevtoolsClient_.setConfiguration((prev) => ({
            ...prev,
            mockApiCalls: prev.mockApiCalls === null ? "fail" : prev.mockApiCalls === "fail" ? "succeed" : null,
        }));

        // Since we are not subscribed to changes in intellsysDevtoolsClient in this component,
        // the UI will be stale unless we trigger a force update
        forceUpdate();
    };

    const cycleShowDebuggingPanels = () => {
        intellsysDevtoolsClient_.setConfiguration((prev) => ({
            ...prev,
            showDebuggingPanels: !prev.showDebuggingPanels,
        }));

        // Since we are not subscribed to changes in intellsysDevtoolsClient in this component,
        // the UI will be stale unless we trigger a force update
        forceUpdate();
    };

    const items: Array<
        {id: Uuid; label: string} & (
            | {
                  onClick?: undefined;
                  hotKey?: undefined;
              }
            | {
                  onClick: () => void;
                  hotKey?: string;
              }
        )
    > = [
        {
            id: uniqueUuid`c4f3bef4-8eeb-4397-8581-cfd525081075`,
            label: "Invalidate router",
            onClick: invalidateRouter,
            hotKey: "shift + s",
        },
        {
            id: uniqueUuid`6ee9e460-75e1-4aa8-8527-1a023769ce24`,
            label: "Toggle theme",
            onClick: toggleTheme,
            hotKey: "shift + d",
        },
        {
            id: uniqueUuid`4788dc5b-ecdc-4d06-a69d-02484d0938e3`,
            label: "Sign out",
            onClick: signOut,
        },
        {
            id: uniqueUuid`bf918545-caeb-4e7e-b689-44f953372c04`,
            label: "--------",
        },
        {
            id: uniqueUuid`6b1d1d61-9dd1-466d-96b9-650127d039b9`,
            label: `Fill dummy data: ${intellsysDevtoolsClient_.getConfiguration().fillDummyData === true ? "On" : "Off"}`,
            onClick: cycleFillDummyData,
        },
        {
            id: uniqueUuid`e7633f3d-9f2a-404f-8e7a-823fb1098df9`,
            label: `Mock API calls: ${intellsysDevtoolsClient_.getConfiguration().mockApiCalls === "succeed" ? "Succeed" : intellsysDevtoolsClient_.getConfiguration().mockApiCalls === "fail" ? "Fail" : "Off"}`,
            onClick: cycleMockApiCalls,
        },
        {
            id: uniqueUuid`d2dae032-9522-46be-a849-b062867a0bd4`,
            label: `Show debugging panels: ${intellsysDevtoolsClient_.getConfiguration().showDebuggingPanels === true ? "On" : "Off"}`,
            onClick: cycleShowDebuggingPanels,
        },
        {
            id: uniqueUuid`293716d7-d5bf-4a14-8330-96d1d6ab460f`,
            label: "--------",
        },
        {
            id: uniqueUuid`ba757e29-2360-4461-8caf-6031ed2e16cf`,
            label: `Insane logs: theming: ${intellsysDevtoolsClient_.getConfiguration().insaneLogs.theming === true ? "On" : "Off"}`,
        },
        {
            id: uniqueUuid`a09c8214-f72e-49fb-86c4-5df4792ef510`,
            label: `Insane logs: tanstackRouterDebugging: ${intellsysDevtoolsClient_.getConfiguration().insaneLogs.tanstackRouterDebugging === true ? "On" : "Off"}`,
        },
        {
            id: uniqueUuid`5d305c1a-0229-41dd-a979-5c87484e26ab`,
            label: `Insane logs: errorComponentLogs: ${intellsysDevtoolsClient_.getConfiguration().insaneLogs.errorComponentLogs === true ? "On" : "Off"}`,
        },
        {
            id: uniqueUuid`5bf811f6-1e2a-4c9a-bfcd-1cec3dc481bd`,
            label: `Insane logs: notFoundComponentLogs: ${intellsysDevtoolsClient_.getConfiguration().insaneLogs.notFoundComponentLogs === true ? "On" : "Off"}`,
        },
    ];

    useHotkeys(
        items
            .filter((item) => item.onClick !== undefined && item.hotKey !== undefined)
            .map((item) => ({
                hotkey: parseHotkey(item.hotKey as string),
                callback: item.onClick as () => void,
            })),
    );

    // return (
    //     <div className="fixed right-0 bottom-[16rem] w-[20rem] group">
    //         <AnankeDropdownMenu<HTMLButtonElement>
    //             trigger={({triggerRef, ...props}) => (
    //                 <button
    //                     type="button"
    //                     {...props}
    //                     // onClick={() => setIsOpen((prev) => !prev)}
    //                     className="text-[0.75rem] leading-normal relative"
    //                     style={{
    //                         writingMode: "vertical-rl",
    //                         transform: "rotate(180deg)",
    //                     }}
    //                     ref={triggerRef}
    //                 >
    //                     intellsys devtools
    //                 </button>
    //             )}
    //         >
    //             <AnankeDropdownItemGroup>
    //                 <ItemBuilder
    //                     items={items}
    //                     itemBuilder={(item) => (
    //                         <button
    //                             key={item.id}
    //                             type="button"
    //                             onClick={item.onClick}
    //                             className={twJoin(
    //                                 "w-full text-left px-2 py-1 bg-ds-surface",
    //                                 item.onClick === undefined ? "cursor-default" : "hover:bg-ds-surface-hovered",
    //                             )}
    //                         >
    //                             {item.label}
    //                         </button>
    //                     )}
    //                 />
    //             </AnankeDropdownItemGroup>
    //         </AnankeDropdownMenu>

    //         {/* className={twJoin(
    //                 "absolute right-[1.125rem] top-0 w-[16rem] grid-cols-1 bg-ds-surface text-ds-text border border-solid border-ds-border",
    //                 isOpen === true ? "grid" : "hidden",
    //             )}
    //         > */}
    //         <ItemBuilder
    //             items={items}
    //             itemBuilder={(item) => (
    //                 <button
    //                     key={item.id}
    //                     type="button"
    //                     onClick={item.onClick}
    //                     className={twJoin(
    //                         "w-full text-left px-2 py-1 bg-ds-surface",
    //                         item.onClick === undefined ? "cursor-default" : "hover:bg-ds-surface-hovered",
    //                     )}
    //                 >
    //                     {item.label}
    //                 </button>
    //             )}
    //         />
    //         {/* </AnankePopup> */}
    //     </div>
    // );

    return (
        <div className="fixed right-0 bottom-[16rem] group">
            <div
                className="text-[0.75rem] leading-normal relative"
                style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                }}
            >
                intellsys devtools
            </div>

            <div className="hidden group-hover:grid absolute right-[1.125rem] top-0 w-[16rem] grid-cols-1 bg-ds-surface text-ds-text border border-solid border-ds-border">
                <ItemBuilder
                    items={items}
                    itemBuilder={(item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={item.onClick}
                            className={twJoin(
                                "w-full text-left px-2 py-1 bg-ds-surface",
                                item.onClick === undefined ? "cursor-default" : "hover:bg-ds-surface-hovered",
                            )}
                        >
                            {item.label}
                        </button>
                    )}
                />
            </div>
        </div>
    );
}
